import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Pencil, Trash2 } from 'lucide-react';
import { Portal } from './Portal';

/**
 * Column definition for TableSelect
 */
export interface TableSelectColumn<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

/**
 * Sort state
 */
type SortDirection = 'asc' | 'desc' | null;

interface SortState<T> {
  key: keyof T | string | null;
  direction: SortDirection;
}

export interface TableSelectProps<T> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  columns: TableSelectColumn<T>[];
  getKey: (item: T) => string | number;
  getDisplayValue: (item: T | null) => string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Called when edit icon is clicked. If provided, an Actions column is rendered. */
  onEdit?: (item: T) => void;
  /** Called when delete icon is clicked. If provided, an Actions column is rendered. */
  onDelete?: (item: T) => void;
}

/**
 * TableSelect - A dropdown that displays options in a sortable table format
 *
 * Features:
 * - Portal-based dropdown for proper z-index layering
 * - Sortable column headers with visual indicators
 * - Mobile-responsive design (bottom sheet on mobile)
 * - Dark theme styling consistent with existing UI
 *
 * @example
 * ```tsx
 * <TableSelect
 *   options={templates}
 *   value={selectedTemplate}
 *   onChange={setSelectedTemplate}
 *   columns={[
 *     { key: 'name', header: 'Template Name', sortable: true },
 *     {
 *       key: 'created_at',
 *       header: 'Created',
 *       sortable: true,
 *       render: (t) => formatDate(t.created_at),
 *       className: 'italic'
 *     }
 *   ]}
 *   getKey={(t) => t.id}
 *   getDisplayValue={(t) => t?.name || 'Select template'}
 * />
 * ```
 */
export function TableSelect<T>({
  options = [],
  value,
  onChange,
  columns,
  getKey,
  getDisplayValue,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  onEdit,
  onDelete,
}: TableSelectProps<T>) {
  const hasActions = Boolean(onEdit || onDelete);
  const [isOpen, setIsOpen] = useState(false);
  const [sortState, setSortState] = useState<SortState<T>>({ key: null, direction: null });
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sort options based on current sort state
  const sortedOptions = useMemo(() => {
    if (!sortState.key || !sortState.direction) {
      return options;
    }

    return [...options].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sortState.key as string];
      const bValue = (b as Record<string, unknown>)[sortState.key as string];

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortState.direction === 'asc' ? -1 : 1;

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortState.direction === 'asc' ? comparison : -comparison;
      }

      // Number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Date string comparison (ISO format)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return sortState.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
      }

      return 0;
    });
  }, [options, sortState]);

  // Handle column header click for sorting
  const handleSort = (column: TableSelectColumn<T>) => {
    if (!column.sortable) return;

    setSortState((prev) => {
      if (prev.key !== column.key) {
        // New column: start with ascending
        return { key: column.key, direction: 'asc' };
      }
      // Same column: cycle through asc -> desc -> null
      if (prev.direction === 'asc') {
        return { key: column.key, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key: column.key, direction: 'asc' };
    });
  };

  // Get sort icon for column
  const getSortIcon = (column: TableSelectColumn<T>) => {
    if (!column.sortable) return null;

    if (sortState.key === column.key) {
      if (sortState.direction === 'asc') {
        return <ChevronUp className="w-3 h-3 text-brand-400" />;
      }
      if (sortState.direction === 'desc') {
        return <ChevronDown className="w-3 h-3 text-brand-400" />;
      }
    }
    return <ChevronsUpDown className="w-3 h-3 text-dark-muted opacity-50" />;
  };

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 300), // Minimum width for table
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedInsideTrigger = selectRef.current?.contains(target);
      const clickedInsideDropdown = dropdownRef.current?.contains(target);

      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (item: T) => {
    onChange(item);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const displayValue = getDisplayValue(value);

  return (
    <div
      ref={selectRef}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-4 py-2 min-h-[44px] md:min-h-0 text-left bg-dark-surface border border-dark-border/30 rounded-xl
          flex items-center justify-between gap-2
          transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-surface/40 active:bg-dark-surface/60'}
          ${isOpen ? 'ring-2 ring-brand-500' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid="table-select-trigger"
        data-ux="action"
        data-interaction-id="table-select-trigger"
      >
        <span className={value ? 'text-dark-text' : 'text-dark-muted'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu - Portal-based with mobile-first responsive design */}
      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed md:absolute"
            style={{
              top: window.innerWidth < 768 ? 'auto' : `${dropdownPosition.top + 8}px`,
              left: window.innerWidth < 768 ? 0 : `${dropdownPosition.left}px`,
              width: window.innerWidth < 768 ? '100%' : `${dropdownPosition.width}px`,
              bottom: window.innerWidth < 768 ? 0 : 'auto',
              zIndex: 99999, // Above modals (z-50) to prevent clipping
            }}
          >
            <div className="bg-dark-surface border-t md:border border-dark-border/30 rounded-t-2xl md:rounded-xl shadow-2xl shadow-black/20 max-h-[70vh] md:max-h-[300px] overflow-auto">
              {/* Table */}
              <table className="w-full">
                {/* Header */}
                <thead className="sticky top-0 bg-dark-header border-b border-dark-border/30">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={String(column.key)}
                        className={`
                          px-3 py-2 text-left text-xs font-medium text-dark-muted uppercase tracking-wider
                          ${column.sortable ? 'cursor-pointer hover:bg-dark-surface/40 select-none' : ''}
                          ${column.headerClassName || ''}
                        `}
                        onClick={() => handleSort(column)}
                      >
                        <div className="flex items-center gap-1">
                          <span>{column.header}</span>
                          {getSortIcon(column)}
                        </div>
                      </th>
                    ))}
                    {hasActions && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-dark-muted uppercase tracking-wider w-20">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                {/* Body */}
                <tbody>
                  {sortedOptions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + (hasActions ? 1 : 0)}
                        className="px-4 py-3 text-sm text-dark-muted text-center"
                      >
                        No options available
                      </td>
                    </tr>
                  ) : (
                    sortedOptions.map((item) => {
                      const key = getKey(item);
                      const isSelected = Boolean(value && getKey(value) === key);

                      return (
                        <tr
                          key={key}
                          onClick={() => handleSelect(item)}
                          className={`
                            cursor-pointer transition-all duration-150
                            ${isSelected ? 'bg-brand-500/10' : 'hover:bg-dark-surface/40 active:bg-dark-surface/60'}
                          `}
                          role="option"
                          aria-selected={isSelected}
                          data-testid={`table-select-option-${key}`}
                          data-ux="action"
                          data-interaction-id={`table-select-option-${key}`}
                        >
                          {columns.map((column) => {
                            const cellValue = (item as Record<string, unknown>)[column.key as string];
                            const displayContent = column.render
                              ? column.render(item)
                              : String(cellValue ?? '');

                            return (
                              <td
                                key={String(column.key)}
                                className={`
                                  px-3 py-2 min-h-[44px] md:min-h-0 text-sm text-dark-text
                                  ${column.className || ''}
                                `}
                              >
                                {displayContent}
                              </td>
                            );
                          })}
                          {hasActions && (
                            <td className="px-3 py-2 min-h-[44px] md:min-h-0 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {onEdit && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onEdit(item);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    className="p-1.5 rounded hover:bg-dark-border transition-all duration-150 text-dark-muted hover:text-brand-400"
                                    title="Edit template"
                                    data-testid={`table-select-edit-${key}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                )}
                                {onDelete && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onDelete(item);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    className="p-1.5 rounded hover:bg-dark-border transition-all duration-150 text-dark-muted hover:text-red-400"
                                    title="Delete template"
                                    data-testid={`table-select-delete-${key}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
